/**
 * PadLab's musical clock.
 *
 * Two ideas carry the whole file.
 *
 * **The position is derived, never accumulated.** Every answer this module gives — the beat
 * a hit lands on, the bar the playhead is in, the absolute time of the next grid line — is
 * computed from `AudioContext.currentTime` against an anchor. A late timer callback, a
 * dropped frame, a tab that was hidden for ten seconds: none of them change where the music
 * is, because nothing here counts. That is the difference between a sequencer that stays
 * locked to the audio for an hour and one that walks away from it in a minute. It matches
 * `TransportService.swift`, which anchors against the host clock for the same reason.
 *
 * **The timer lives in a Worker.** Chris Wilson's look-ahead pattern needs a timer that
 * fires every 25 ms so it can schedule the next 150 ms of audio ahead of the playhead. A
 * main-thread `setInterval` is clamped to roughly 1 Hz by Safari and Chrome once the tab is
 * hidden, which would starve the scheduler and drop every note in the gap. `setInterval`
 * inside a dedicated worker is not clamped the same way, so the schedule survives an
 * occluded tab.
 *
 * That is worth being precise about, because it is easy to oversell: **on iOS the audio
 * session is suspended when the app leaves the foreground regardless of what any timer
 * does.** `AudioContext.currentTime` stops advancing, the scheduler correctly schedules
 * nothing, and playback resumes from where the audio clock stopped when the user comes
 * back. The worker timer buys correct behaviour for a partially occluded or backgrounded
 * *tab* on desktop and for the brief moments iOS keeps audio alive; it does not and cannot
 * buy background playback on an iPhone.
 */

import { createLogger } from '../core/log';
import { signal, type ReadonlySignal, type Signal } from '../core/signal';
import { clampFinite, quantizeBeats, type Quantize } from '../model/types';

const log = createLogger('transport');

/** How often the scheduler wakes up. */
export const TICK_INTERVAL_MS = 25;

/** How far ahead of the playhead each wake-up schedules. Six ticks of slack. */
export const LOOKAHEAD_SECONDS = 0.15;

/** Position updates per second. Fast enough to read as continuous, cheap enough to ignore. */
export const POSITION_HZ = 30;

export const TEMPO_MINIMUM = 20;
export const TEMPO_MAXIMUM = 300;

/** Taps further apart than this start a new count, they do not mean a slower tempo. */
const MAXIMUM_TAP_GAP_MS = 2500;
const TAP_WINDOW = 5;

/**
 * A grid line landing inside this window has already gone past the scheduler, so the next
 * one is chosen instead. Without it a quantised trigger would be scheduled in the past and
 * play late rather than on the grid.
 */
const MINIMUM_SCHEDULE_LEAD = 0.005;

/**
 * Playback starts this far in the future so the first step of a pattern is genuinely
 * schedulable. Anchoring exactly at `currentTime` would put beat zero in the past by the
 * time the first tick runs, and the first hit of every pattern would be a few milliseconds
 * late.
 */
const START_LEAD = 0.02;

// MARK: - Worker clock

/**
 * The tick worker. Deliberately tiny and dependency-free: it is compiled into a string,
 * turned into a Blob and loaded as a classic worker, which needs no build-system support
 * and no second entry point. `worker-src 'self' blob:` in the page's CSP is what allows it.
 */
const TICK_WORKER_SOURCE = `let handle = null;
let interval = 25;
function restart() {
  if (handle !== null) clearInterval(handle);
  handle = setInterval(function () { self.postMessage(0); }, interval);
}
self.onmessage = function (event) {
  const message = event.data;
  if (message === null || typeof message !== 'object') return;
  if (typeof message.interval === 'number' && message.interval > 0) interval = message.interval;
  if (message.type === 'start') restart();
  else if (message.type === 'stop') { if (handle !== null) clearInterval(handle); handle = null; }
  else if (message.type === 'interval' && handle !== null) restart();
};
`;

/**
 * A repeating timer that keeps its cadence when the page is hidden.
 *
 * Falls back to a main-thread `setInterval` when Workers or blob URLs are unavailable —
 * a hardened enterprise CSP, or a browser we have not met. The fallback ticks correctly in
 * the foreground and is throttled in the background, which degrades the same way a naive
 * implementation would rather than failing outright.
 */
export class WorkerClock {
  private worker: Worker | null = null;
  private objectURL: string | null = null;
  private fallbackHandle: ReturnType<typeof setInterval> | null = null;
  private handler: (() => void) | null = null;
  private intervalMs: number;
  private disposed = false;

  /** False when the clock had to fall back to a main-thread timer. */
  readonly usesWorker: boolean;

  constructor(intervalMs = TICK_INTERVAL_MS) {
    this.intervalMs = Math.max(1, Math.round(intervalMs));
    this.usesWorker = this.createWorker();
  }

  private createWorker(): boolean {
    if (typeof Worker === 'undefined' || typeof Blob === 'undefined' || typeof URL === 'undefined') {
      return false;
    }
    try {
      const blob = new Blob([TICK_WORKER_SOURCE], { type: 'text/javascript' });
      const url = URL.createObjectURL(blob);
      const worker = new Worker(url);
      worker.onmessage = () => {
        this.handler?.();
      };
      // A worker that fails to start (blocked object URL, out of memory) must not take
      // the transport down with it — drop back to the main-thread timer instead.
      worker.onerror = (event) => {
        log.warn('tick worker failed, falling back to a main-thread timer');
        event.preventDefault();
        this.teardownWorker();
        if (this.handler !== null) this.startFallback();
      };
      this.worker = worker;
      this.objectURL = url;
      return true;
    } catch (error) {
      log.error('tick worker unavailable, using a main-thread timer', error);
      return false;
    }
  }

  private teardownWorker(): void {
    this.worker?.terminate();
    this.worker = null;
    if (this.objectURL !== null) {
      URL.revokeObjectURL(this.objectURL);
      this.objectURL = null;
    }
  }

  private startFallback(): void {
    if (this.fallbackHandle !== null) clearInterval(this.fallbackHandle);
    this.fallbackHandle = setInterval(() => this.handler?.(), this.intervalMs);
  }

  start(handler: () => void): void {
    if (this.disposed) return;
    this.handler = handler;
    if (this.worker !== null) {
      this.worker.postMessage({ type: 'start', interval: this.intervalMs });
      return;
    }
    this.startFallback();
  }

  stop(): void {
    this.handler = null;
    this.worker?.postMessage({ type: 'stop' });
    if (this.fallbackHandle !== null) {
      clearInterval(this.fallbackHandle);
      this.fallbackHandle = null;
    }
  }

  setInterval(intervalMs: number): void {
    const next = Math.max(1, Math.round(intervalMs));
    if (next === this.intervalMs) return;
    this.intervalMs = next;
    this.worker?.postMessage({ type: 'interval', interval: next });
    if (this.fallbackHandle !== null && this.handler !== null) this.startFallback();
  }

  dispose(): void {
    this.disposed = true;
    this.stop();
    this.teardownWorker();
  }
}

// MARK: - Metronome

/**
 * The click. It lives with the clock because both the sequencer and the recorder's
 * count-in need it, and a count-in the user cannot hear is not a count-in.
 *
 * Two oscillators are created per click and thrown away. That is deliberate: a
 * `BufferSource`-style pool would save two allocations twice a second on the *main*
 * thread, where they cost nothing, while making the level and pitch of the accent harder
 * to change. Nothing here runs on the audio thread.
 */
export class Metronome {
  private readonly context: BaseAudioContext;
  private readonly output: GainNode;

  constructor(context: BaseAudioContext, destination?: AudioNode) {
    this.context = context;
    this.output = context.createGain();
    this.output.gain.value = 1;
    this.output.connect(destination ?? context.destination);
  }

  /** Schedules one click at an absolute context time. */
  schedule(time: number, accent: boolean, volume: number): void {
    const level = clampFinite(volume, 0, 1, 0.6);
    if (level <= 0) return;
    const at = Math.max(time, this.context.currentTime);

    const oscillator = this.context.createOscillator();
    const body = this.context.createOscillator();
    const gain = this.context.createGain();

    oscillator.type = 'square';
    oscillator.frequency.value = accent ? 1800 : 1200;
    // A second sine an octave down gives the click a body, so it survives a phone speaker
    // that rolls off everything above 8 kHz at low volume.
    body.type = 'sine';
    body.frequency.value = accent ? 900 : 600;

    const peak = level * (accent ? 0.5 : 0.32);
    gain.gain.setValueAtTime(0.0001, at);
    gain.gain.exponentialRampToValueAtTime(peak, at + 0.001);
    gain.gain.exponentialRampToValueAtTime(0.0001, at + (accent ? 0.055 : 0.04));

    oscillator.connect(gain);
    body.connect(gain);
    gain.connect(this.output);

    const stopAt = at + 0.08;
    oscillator.start(at);
    body.start(at);
    oscillator.stop(stopAt);
    body.stop(stopAt);
    oscillator.onended = () => {
      oscillator.disconnect();
      body.disconnect();
      gain.disconnect();
    };
  }

  setVolume(volume: number): void {
    this.output.gain.value = clampFinite(volume, 0, 1, 1);
  }

  dispose(): void {
    this.output.disconnect();
  }
}

// MARK: - Position

export interface TransportPosition {
  readonly isPlaying: boolean;
  /** 1-based, for display. */
  readonly bar: number;
  /** 1-based within the bar. */
  readonly beat: number;
  /** 0-based step within the beat. */
  readonly step: number;
  /** Absolute musical position in quarter notes. Never negative. */
  readonly beats: number;
  /** 0…1 through the pattern currently sounding. */
  readonly patternProgress: number;
  readonly isCountingIn: boolean;
  readonly countInBeatsRemaining: number;
}

export const STOPPED_POSITION: TransportPosition = Object.freeze({
  isPlaying: false,
  bar: 1,
  beat: 1,
  step: 0,
  beats: 0,
  patternProgress: 0,
  isCountingIn: false,
  countInBeatsRemaining: 0,
});

function samePosition(a: TransportPosition, b: TransportPosition): boolean {
  return (
    a.isPlaying === b.isPlaying &&
    a.bar === b.bar &&
    a.beat === b.beat &&
    a.step === b.step &&
    a.isCountingIn === b.isCountingIn &&
    a.countInBeatsRemaining === b.countInBeatsRemaining &&
    Math.abs(a.beats - b.beats) < 1e-6 &&
    Math.abs(a.patternProgress - b.patternProgress) < 1e-4
  );
}

/** The window a scheduler subscriber is asked to fill. Both values are absolute ctx times. */
export interface ScheduleWindow {
  readonly now: number;
  readonly horizon: number;
}

export type ScheduleHandler = (window: ScheduleWindow) => void;

// MARK: - Transport

export class Transport {
  private readonly context: BaseAudioContext;
  private readonly clock: WorkerClock;

  private readonly positionSignal: Signal<TransportPosition> = signal(STOPPED_POSITION);
  private readonly playingSignal: Signal<boolean> = signal(false);
  private readonly tempoSignal: Signal<number> = signal(96);
  private readonly swingSignal: Signal<number> = signal(0);
  private readonly tapCountSignal: Signal<number> = signal(0);

  /** Beat number `anchorTime` corresponds to. Negative during a count-in. */
  private anchorBeat = 0;
  private anchorTime = 0;

  private patternStartBeat = 0;
  private patternLengthBeats = 4;

  private stepsPerBeatValue = 4;
  private beatsPerBarValue = 4;
  private countInBeatsValue = 0;

  private tapTimes: number[] = [];
  private readonly handlers = new Set<ScheduleHandler>();
  private positionAccumulator = 0;
  private disposed = false;

  constructor(context: BaseAudioContext, clock: WorkerClock = new WorkerClock(TICK_INTERVAL_MS)) {
    this.context = context;
    this.clock = clock;
  }

  // MARK: Observable state

  get position(): ReadonlySignal<TransportPosition> {
    return this.positionSignal;
  }

  get playing(): ReadonlySignal<boolean> {
    return this.playingSignal;
  }

  get tempo(): ReadonlySignal<number> {
    return this.tempoSignal;
  }

  get swing(): ReadonlySignal<number> {
    return this.swingSignal;
  }

  /** Taps collected so far, so the tap button can show progress instead of feeling dead. */
  get tapCount(): ReadonlySignal<number> {
    return this.tapCountSignal;
  }

  get isPlaying(): boolean {
    return this.playingSignal.peek();
  }

  get secondsPerBeat(): number {
    return 60 / this.tempoSignal.peek();
  }

  get secondsPerStep(): number {
    return this.secondsPerBeat / Math.max(1, this.stepsPerBeatValue);
  }

  get stepsPerBeat(): number {
    return this.stepsPerBeatValue;
  }

  get beatsPerBar(): number {
    return this.beatsPerBarValue;
  }

  get countInBeats(): number {
    return this.countInBeatsValue;
  }

  /** True when the tick timer survives a hidden tab. Surfaced in the capability matrix. */
  get hasBackgroundSafeClock(): boolean {
    return this.clock.usesWorker;
  }

  // MARK: Control

  play(): void {
    if (this.disposed || this.isPlaying) return;
    this.anchorBeat = -this.countInBeatsValue;
    this.anchorTime = this.context.currentTime + START_LEAD;
    this.playingSignal.value = true;
    this.positionAccumulator = 0;
    this.clock.start(() => this.tick());
    this.publishPosition();
  }

  stop(): void {
    this.clock.stop();
    if (!this.isPlaying && samePosition(this.positionSignal.peek(), STOPPED_POSITION)) return;
    this.playingSignal.value = false;
    this.anchorBeat = 0;
    this.anchorTime = this.context.currentTime;
    this.patternStartBeat = 0;
    this.positionSignal.value = STOPPED_POSITION;
  }

  toggle(): void {
    if (this.isPlaying) this.stop();
    else this.play();
  }

  setTempo(bpm: number): void {
    const clamped = clampFinite(bpm, TEMPO_MINIMUM, TEMPO_MAXIMUM, 96);
    if (clamped === this.tempoSignal.peek()) return;
    if (this.isPlaying) {
      // Hold the playhead still across the change. One reading of the clock feeds both
      // halves of the anchor, so the re-anchor is exact and nothing already scheduled
      // moves; only music from here on is re-timed.
      const now = this.context.currentTime;
      this.anchorBeat = this.beatsAtTime(now);
      this.anchorTime = now;
    }
    this.tempoSignal.value = clamped;
    this.publishPosition();
  }

  /**
   * One tap of the tempo button. The tempo is the median of the last five intervals: a
   * mean would let a single fumbled tap drag the whole tempo with it, while the median
   * simply ignores it.
   */
  tapTempo(): number | null {
    const now = performance.now();
    const last = this.tapTimes[this.tapTimes.length - 1];
    if (last !== undefined && now - last > MAXIMUM_TAP_GAP_MS) this.tapTimes.length = 0;
    this.tapTimes.push(now);
    if (this.tapTimes.length > TAP_WINDOW) this.tapTimes.splice(0, this.tapTimes.length - TAP_WINDOW);
    this.tapCountSignal.value = this.tapTimes.length;

    if (this.tapTimes.length < 2) return null;
    const intervals: number[] = [];
    for (let index = 1; index < this.tapTimes.length; index++) {
      intervals.push(this.tapTimes[index]! - this.tapTimes[index - 1]!);
    }
    intervals.sort((a, b) => a - b);
    const middle = intervals.length >> 1;
    const median =
      intervals.length % 2 === 1
        ? intervals[middle]!
        : (intervals[middle - 1]! + intervals[middle]!) * 0.5;
    if (median <= 0.1) return null;
    const bpm = 60000 / median;
    this.setTempo(bpm);
    return this.tempoSignal.peek();
  }

  resetTapTempo(): void {
    this.tapTimes.length = 0;
    this.tapCountSignal.value = 0;
  }

  setSwing(amount: number): void {
    const clamped = clampFinite(amount, 0, 0.75, 0);
    if (clamped === this.swingSignal.peek()) return;
    this.swingSignal.value = clamped;
  }

  setStepsPerBeat(value: number): void {
    const clamped = Math.round(clampFinite(value, 1, 8, 4));
    if (clamped === this.stepsPerBeatValue) return;
    this.stepsPerBeatValue = clamped;
    this.publishPosition();
  }

  setCountIn(bars: number, beatsPerBar: number): void {
    this.beatsPerBarValue = Math.round(clampFinite(beatsPerBar, 1, 16, 4));
    this.countInBeatsValue = Math.max(0, Math.round(clampFinite(bars, 0, 8, 0))) * this.beatsPerBarValue;
    this.publishPosition();
  }

  /**
   * Told by the sequencer whenever a new pass begins, so `patternProgress` tracks the
   * pattern that is actually sounding instead of assuming everything starts at beat zero.
   */
  setPatternWindow(startBeat: number, lengthInBeats: number): void {
    if (!Number.isFinite(startBeat) || !Number.isFinite(lengthInBeats) || lengthInBeats <= 0) return;
    this.patternStartBeat = startBeat;
    this.patternLengthBeats = lengthInBeats;
    this.publishPosition();
  }

  // MARK: Grid

  /** Beat position at an arbitrary context time. Negative during the count-in. */
  beatsAtTime(time: number): number {
    if (!this.isPlaying) return 0;
    return this.anchorBeat + (time - this.anchorTime) / this.secondsPerBeat;
  }

  /** Beat position right now, uncached — what a live hit has to be timestamped against. */
  get liveBeats(): number {
    return this.beatsAtTime(this.context.currentTime);
  }

  /** Absolute context time of a beat on the current timeline. */
  timeForBeat(beat: number): number | null {
    if (!this.isPlaying || !Number.isFinite(beat)) return null;
    return this.anchorTime + (beat - this.anchorBeat) * this.secondsPerBeat;
  }

  /**
   * Absolute context time of the next `division` grid line. `null` when quantisation is off
   * or the transport is stopped — a stopped transport has no timeline to place anything on,
   * and the caller is expected to play immediately instead.
   */
  nextQuantizedTime(division: Quantize): number | null {
    const gridBeats = quantizeBeats(division);
    if (!this.isPlaying || gridBeats === null || gridBeats <= 0) return null;
    const now = this.context.currentTime;
    const search = this.beatsAtTime(now) + MINIMUM_SCHEDULE_LEAD / this.secondsPerBeat;
    const line = Math.floor(search / gridBeats) + 1;
    return this.timeForBeat(line * gridBeats);
  }

  /** Beats from now until that grid line — what a "queued" pad badge counts down. */
  beatsUntilQuantized(division: Quantize): number | null {
    const time = this.nextQuantizedTime(division);
    if (time === null) return null;
    return Math.max(0, (time - this.context.currentTime) / this.secondsPerBeat);
  }

  // MARK: Scheduling

  /**
   * Registers a look-ahead subscriber. It is called every 25 ms while the transport runs
   * and must schedule everything that falls before `horizon`, then return; anything it
   * leaves behind will be offered again on the next tick.
   */
  onSchedule(handler: ScheduleHandler): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  private tick(): void {
    if (this.disposed || !this.isPlaying) return;
    const now = this.context.currentTime;
    const window: ScheduleWindow = { now, horizon: now + LOOKAHEAD_SECONDS };

    for (const handler of [...this.handlers]) {
      try {
        handler(window);
      } catch (error) {
        // One broken subscriber must not stop the clock for the others: the sequencer and
        // the metronome share this tick, and losing the metronome should not lose the beat.
        log.error('a scheduler subscriber threw; continuing', error);
      }
    }

    // 25 ms ticks carrying a 33.3 ms publish budget works out at exactly three publishes
    // in every four ticks — 30 Hz — without a second timer.
    this.positionAccumulator += TICK_INTERVAL_MS;
    const publishInterval = 1000 / POSITION_HZ;
    if (this.positionAccumulator >= publishInterval) {
      this.positionAccumulator -= publishInterval;
      this.publishPosition();
    }
  }

  private publishPosition(): void {
    if (!this.isPlaying) {
      if (!samePosition(this.positionSignal.peek(), STOPPED_POSITION)) {
        this.positionSignal.value = STOPPED_POSITION;
      }
      return;
    }

    const raw = this.beatsAtTime(this.context.currentTime);
    const isCountingIn = raw < 0;
    const musical = Math.max(0, raw);
    const barLength = Math.max(1, this.beatsPerBarValue);

    const barIndex = Math.floor(musical / barLength);
    const beatInBar = Math.floor(musical - barIndex * barLength);
    const fraction = musical - Math.floor(musical);
    const stepInBeat = Math.min(this.stepsPerBeatValue - 1, Math.floor(fraction * this.stepsPerBeatValue));

    let progress = 0;
    if (this.patternLengthBeats > 0) {
      const intoPattern = musical - this.patternStartBeat;
      if (intoPattern >= 0) progress = (intoPattern % this.patternLengthBeats) / this.patternLengthBeats;
    }

    const next: TransportPosition = {
      isPlaying: true,
      bar: barIndex + 1,
      beat: beatInBar + 1,
      step: Math.max(0, stepInBeat),
      beats: musical,
      patternProgress: Math.min(Math.max(progress, 0), 1),
      isCountingIn,
      // Capped at the configured length: playback is anchored a hair in the future so the
      // first step is schedulable, which without the cap would make a one-bar count-in
      // briefly display "5".
      countInBeatsRemaining: isCountingIn ? Math.min(this.countInBeatsValue, Math.ceil(-raw)) : 0,
    };
    if (!samePosition(next, this.positionSignal.peek())) this.positionSignal.value = next;
  }

  dispose(): void {
    this.stop();
    this.disposed = true;
    this.handlers.clear();
    this.clock.dispose();
  }
}
